import { useState, useCallback, useEffect } from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import {
  magicLinkRequest,
  MagicLinkRequestResponse,
} from '../../../../api/auth/auth.api';

type State =
  | { status: 'idle' }
  | { status: 'sending' }
  | { status: 'sent'; email: string }
  | { status: 'sso_required'; ssoUrl?: string }
  | { status: 'rate_limited' }
  | { status: 'error' };

// Seconds the resend button stays disabled after a successful send, so users
// can't spam requests (and quietly hit the backend per-email rate limit, which
// returns success but drops the email). Surfaced via `cooldownSeconds`.
const RESEND_COOLDOWN_SECONDS = 60;

export function useMagicLink() {
  const [state, setState] = useState<State>({ status: 'idle' });
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  // Tick the cooldown down to zero. Re-scheduled each second via the
  // cooldownSeconds dependency; the timeout is cleared on unmount / restart.
  useEffect(() => {
    if (cooldownSeconds <= 0) return undefined;
    const timer = setTimeout(() => {
      setCooldownSeconds((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearTimeout(timer);
  }, [cooldownSeconds]);

  const send = useCallback(async (email: string, captchaToken: string) => {
    setState({ status: 'sending' });
    try {
      const resp: AxiosResponse<MagicLinkRequestResponse> =
        await magicLinkRequest({
          data: { email, captchaToken },
        });
      const data = resp.data;
      if (data.status === 'sso_required') {
        // Backend has signaled this email needs SSO. Two cases:
        //   - ssoUrl present: redirect straight to the SSO IdP.
        //   - ssoUrl absent: SsoService has no URL builder yet (see FIR-20770);
        //     surface an SSO-required UI state so the user can re-route via
        //     /login (IntakeForm will re-run the SSO check and dispatch them
        //     properly). Falling through to the success state would mislead
        //     them with a "Check your inbox" message when no email was sent.
        setState({ status: 'sso_required', ssoUrl: data.ssoUrl });
        if (data.ssoUrl) {
          window.location.href = data.ssoUrl;
        }
        return;
      }
      setState({ status: 'sent', email });
      // Start the resend cooldown on a successful send.
      setCooldownSeconds(RESEND_COOLDOWN_SECONDS);
    } catch (e) {
      const status = (e as AxiosError)?.response?.status;
      if (status === 429) setState({ status: 'rate_limited' });
      else setState({ status: 'error' });
    }
  }, []);

  return { state, send, cooldownSeconds };
}
